/**
 * The words and time formats the alert list speaks, shared so the local and the cloud list
 * can't label the same row two different ways.
 */

/**
 * What the alert is CALLED.
 *
 * `alert_name` is the node's own answer, decided when the alert fired and stored on the row
 * (migration 056). It is the ONLY source that can be right for a watchlist hit: a flagged
 * person's alert is "Missing Person" or "Wanted Person" depending on why they are listed, and
 * that is a fact about the watchlist entry, not about whichever rule happened to match the
 * face event. Reading the rule's name instead is what put "Face Recognized" over a missing
 * child and "Vehicle Crossing" over a stolen car — live broadcasts carried `alert_name` and
 * got it right, and the same alert came back from the database wearing the wrong name.
 *
 * The two fallbacks are for rows written BEFORE that column existed: the matched rule's name,
 * then a Title-Cased event type, which is what an operator would call an unruled alert anyway.
 */
export function alertName(a: any): string {
  if (a?.alert_name) return a.alert_name;
  if (a?.alert_config_name) return a.alert_config_name;
  const ev = a?.event_type || '';
  return ev ? ev.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : 'Alert';
}

/** Alert timestamps arrive as epoch SECONDS from the node, epoch ms from some callers. */
export function alertTimestampMs(ts: any): number {
  if (ts == null || ts === '') return NaN;
  let ms = Number(ts);
  if (!Number.isFinite(ms)) return new Date(ts).getTime();
  if (ms < 1e12) ms *= 1000;
  return ms;
}

/**
 * The list's "when" column, split in two: the clock reading on top and the day under it.
 *
 * Alerts are worked newest-first and almost always on the same day, so the time is the part
 * that distinguishes rows and the date is the part that only matters at the boundary — one
 * run-on `13/08/2026, 2:22:07 pm` per row made every row look identical at a glance.
 */
export function alertWhen(ts: any): { time: string; date: string; full: string } {
  const ms = alertTimestampMs(ts);
  if (Number.isNaN(ms)) return { time: '—', date: '', full: '' };
  const d = new Date(ms);
  return {
    time: d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase(),
    date: d.toLocaleDateString([], { day: '2-digit', month: 'short' }),
    full: d.toLocaleString(),
  };
}

/**
 * `2:22 PM · 13 Aug` — the one-line stamp the CARD views print over a frame.
 *
 * The clock first, because same-day rows differ in that part, and the date kept because a
 * list filtered to "all time" is not all one day. Upper-cased: `2:22 pm` beside the bold
 * uppercase chip next to it reads as a different typographic register. The table keeps
 * `alertWhen`'s two lines instead — it has a column to stack them in.
 */
export function whenStamp(ts: any): string {
  const when = alertWhen(ts);
  if (when.time === '—') return '';
  return when.date ? `${when.time.toUpperCase()} · ${when.date}` : when.time.toUpperCase();
}

/**
 * The alert's picture, as a URL a browser can load DIRECTLY — or null.
 *
 * Alerts carry `image_urls`, a map of object-store URLs per crop ({ fullFrame, personCrop,
 * faceCrop }) written when the row is persisted. `fullFrame` is the whole scene, which is what
 * a thumbnail wants: a crop out of context tells an operator nothing about where the thing
 * was. Any other crop still beats no image, so a model that ships only a crop gets one.
 *
 * Directly-loadable is the caveat that decides who may use this. On a node the URLs point at
 * storage the browser can reach; from the cloud they point INSIDE the node and resolve to
 * nothing, which is why the cloud streams the same images through its tunnel proxy instead
 * (see AlertListTable's `loadThumbnail`). Callers pick the one their transport supports —
 * this function does not know which side it is running on.
 */
export function alertImageUrl(alert: any): string | null {
  const urls = alert?.image_urls && typeof alert.image_urls === 'object' ? alert.image_urls : null;
  if (!urls) return null;
  const best = urls.fullFrame || Object.values(urls).find((u) => typeof u === 'string' && u);
  return (best as string) || null;
}
